// ── Catálogo de planes (2026-08-19) ──────────────────────────────────────────
// Los planes se definen UNA VEZ acá, no por cuenta. Antes había que pegar un
// stripe_price_id a mano en cada cliente para que pudiera pagar; un cliente
// nuevo no podía suscribirse solo.
//
// Detail Page Pattern: DetailSection + KVRow + SectionHeaderAction, igual que
// el detalle de cuenta. Sin componentes nuevos.
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { DetailSection } from '../components/ui/DetailSection.js';
import { SectionHeaderAction } from '../components/ui/SectionHeaderAction.js';
import { KVRow } from '../components/ui/KVRow.js';
import { Modal } from '../components/ui/Modal.js';
import { Button } from '../components/ui/Button.js';
import { Input } from '../components/ui/Input.js';
import { Field } from '../components/ui/Field.js';
import { Toggle } from '../components/ui/Toggle.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { ListState } from '../components/ui/ListState.js';
import { useToast } from '../components/ui/Toast.js';
import { ApiError } from '../lib/api.js';
import {
  usePlans, useCreatePlan, useUpdatePlan, usePlanMigrationPreview, useMigratePlan,
  type PlatformPlan,
} from '../hooks/usePlatformAccounts.js';

function fmtPrice(p: PlatformPlan, dash: string): string {
  if (p.amount == null) return dash;
  const money = new Intl.NumberFormat('es', { style: 'currency', currency: (p.currency ?? 'usd').toUpperCase() })
    .format(p.amount / 100);
  return p.interval ? `${money} / ${p.interval}` : money;
}

// ── Alta / edición ───────────────────────────────────────────────────────────

function PlanEditor({ plan, onClose }: { plan: PlatformPlan | null; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const create = useCreatePlan();
  const update = useUpdatePlan();
  const editing = plan != null;

  const [id, setId] = useState(plan?.id ?? '');
  const [name, setName] = useState(plan?.name ?? '');
  const [description, setDescription] = useState(plan?.description ?? '');
  const [priceId, setPriceId] = useState(plan?.stripePriceId ?? '');
  const [sortOrder, setSortOrder] = useState(String(plan?.sortOrder ?? 0));

  async function save() {
    const payload = {
      name: name.trim(),
      description: description.trim() || null,
      stripePriceId: priceId.trim(),
      sortOrder: Number(sortOrder) || 0,
    };
    try {
      // El backend valida el price contra Stripe antes de guardar, así que un
      // id mal tipeado se rechaza acá y no cuando un cliente va a pagar.
      if (editing) await update.mutateAsync({ id: plan.id, ...payload });
      else await create.mutateAsync({ id: id.trim(), ...payload });
      toast.success(t('platformAdmin.plans.saved'));
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? t('platformAdmin.plans.editTitle') : t('platformAdmin.plans.newTitle')}
      size="sm"
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="solid" tone="cyan" loading={pending} onClick={() => { void save(); }}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-4)' }}>
        {/* El id es la identidad del plan y no se cambia después: cambiarlo
            dejaría sin nombre a lo que ya se está cobrando. */}
        {!editing && (
          <Field label={t('platformAdmin.plans.idLabel')} hint={t('platformAdmin.plans.idHint')}>
            <Input value={id} onChange={setId} placeholder="starter" fullWidth autoFocus />
          </Field>
        )}
        <Field label={t('platformAdmin.plans.nameLabel')}>
          <Input value={name} onChange={setName} fullWidth {...(editing ? { autoFocus: true } : {})} />
        </Field>
        <Field label={t('platformAdmin.plans.descriptionLabel')} hint={t('platformAdmin.plans.descriptionHint')}>
          <Input value={description} onChange={setDescription} fullWidth />
        </Field>
        <Field label={t('platformAdmin.plans.priceLabel')} hint={t('platformAdmin.plans.priceHint')}>
          <Input value={priceId} onChange={setPriceId} placeholder="price_..." fullWidth />
        </Field>
        <Field label={t('platformAdmin.plans.orderLabel')} hint={t('platformAdmin.plans.orderHint')}>
          <Input value={sortOrder} onChange={setSortOrder} type="number" fullWidth />
        </Field>
      </div>
    </Modal>
  );
}

// ── Migración de suscriptores ────────────────────────────────────────────────
// Dos pasos, a propósito: cambiar el precio de un plan NO mueve a quien ya
// paga, porque su suscripción en Stripe conserva el price con el que se creó.
// Moverlo automáticamente sería un cobro sorpresa. Primero se ve a cuántos
// afecta y desde cuándo; recién después se aplica.
function MigrationModal({ planId, onClose }: { planId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isLoading, isError, error } = usePlanMigrationPreview(planId);
  const migrate = useMigratePlan();
  const dash = t('platformAdmin.noData');

  async function apply() {
    if (!data?.affected.length) return;
    try {
      // Se mandan las cuentas EXACTAS que se listaron: si alguien entró o salió
      // entre la vista previa y el clic, no se migra a quien no estuvo a la vista.
      const r = await migrate.mutateAsync({ planId, accountIds: data.affected.map(a => a.accountId) });
      toast.success(t('platformAdmin.plans.migrateDone', { count: r.migrated.length }));
      if (r.failed.length > 0) toast.error(t('platformAdmin.plans.migrateFailed', { count: r.failed.length }));
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={t('platformAdmin.plans.migrateTitle')}
      size="md"
      footer={
        <>
          <Button variant="outline" tone="muted" onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="solid" tone="cyan"
            loading={migrate.isPending}
            disabled={!data?.affected.length}
            onClick={() => { void apply(); }}
          >
            {t('platformAdmin.plans.migrateBtn', { count: data?.affected.length ?? 0 })}
          </Button>
        </>
      }
    >
      <ListState
        isLoading={isLoading}
        isError={isError}
        error={error}
        isEmpty={data != null && data.affected.length === 0}
        emptyTitle={t('platformAdmin.plans.migrateNobody')}
        size="sm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
          <p style={{ fontSize: 'var(--evo-fs-body)', color: 'var(--evo-fg-3)', margin: 0, lineHeight: 1.6 }}>
            {t('platformAdmin.plans.migrateExplain')}
          </p>
          {data?.affected.map(a => (
            <KVRow
              key={a.accountId}
              label={a.name}
              value={a.effectiveFrom
                ? t('platformAdmin.plans.effectiveFrom', { date: new Date(a.effectiveFrom).toLocaleDateString('es') })
                : dash}
            />
          ))}
        </div>
      </ListState>
    </Modal>
  );
}

// ── Pantalla ─────────────────────────────────────────────────────────────────

export function PlatformPlansPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const toast = useToast();
  const { data, isLoading, isError, error } = usePlans();
  const update = useUpdatePlan();
  const [editing, setEditing] = useState<PlatformPlan | null>(null);
  const [creating, setCreating] = useState(false);
  const [migrating, setMigrating] = useState<string | null>(null);
  const dash = t('platformAdmin.noData');

  useEffect(() => {
    setOverride({
      title: t('platformAdmin.plans.title'),
      subtitle: t('platformAdmin.plans.subtitle'),
      // REGLA #7: variante -large (viewBox 0 0 56 56).
      heroIcon: 'plans',
      heroIconColor: 'var(--evo-cyan)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="cyan" icon="plus" onClick={() => setCreating(true)}>
            {t('platformAdmin.plans.newBtn')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [t, setOverride]);

  const plans = useMemo(() => data?.plans ?? [], [data]);

  async function toggleActive(plan: PlatformPlan, active: boolean) {
    try {
      await update.mutateAsync({ id: plan.id, active });
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : String(err));
    }
  }

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 'var(--evo-space-6) var(--evo-page-pad-x)' }}>
      <div style={{ maxWidth: '900px' }}>
        <ListState
            isLoading={isLoading}
            isError={isError}
            error={error}
            isEmpty={plans.length === 0}
            emptyTitle={t('platformAdmin.plans.empty')}
            emptyDescription={t('platformAdmin.plans.emptyHint')}
          >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {plans.map(plan => (
                <DetailSection
                  key={plan.id}
                  title={plan.name}
                  titleRight={
                    <>
                      <Pill size="xs" variant="outline" tone={plan.active ? 'success' : 'muted'}>
                        {plan.active ? t('platformAdmin.plans.active') : t('platformAdmin.plans.inactive')}
                      </Pill>
                      <SectionHeaderAction onClick={() => setEditing(plan)}>{t('common.edit', 'Editar')}</SectionHeaderAction>
                    </>
                  }
                >
                  {/* El monto viene de Stripe, no de nuestra DB: si no se pudo
                      resolver se muestra "—", nunca un número inventado. */}
                  <KVRow label={t('platformAdmin.plans.priceLabel')} value={fmtPrice(plan, dash)} />
                  <KVRow label={t('platformAdmin.plans.priceIdLabel')} value={plan.stripePriceId} />
                  {plan.description && <KVRow label={t('platformAdmin.plans.descriptionLabel')} value={plan.description} />}
                  <KVRow
                    label={t('platformAdmin.plans.activeLabel')}
                    value={
                      <Toggle
                        checked={plan.active}
                        onChange={next => { void toggleActive(plan, next); }}
                        description={t('platformAdmin.plans.activeHelp')}
                        size="sm"
                        disabled={update.isPending}
                      />
                    }
                  />
                  <KVRow
                    label={t('platformAdmin.plans.migrateLabel')}
                    value={
                      <SectionHeaderAction onClick={() => setMigrating(plan.id)}>
                        {t('platformAdmin.plans.migrateOpen')}
                      </SectionHeaderAction>
                    }
                  />
                </DetailSection>
              ))}
            </div>
          </ListState>
      </div>

      {creating && <PlanEditor plan={null} onClose={() => setCreating(false)} />}
      {editing && <PlanEditor plan={editing} onClose={() => setEditing(null)} />}
      {migrating && <MigrationModal planId={migrating} onClose={() => setMigrating(null)} />}
    </div>
  );
}
